import type {
  ICoord2D,
  ILineSegment,
  IMinMax,
  IRect,
  ITileGrid,
  ITileGridCell,
  ITileGridCoord,
  ITileGridRange,
  TileVisibilitySampleDensity
} from '@nova/types';

import { GeomUtils } from './geom-utils';
import { tileIndexAt, tileIndexToColRow } from './map-gen/tile-grid';

export interface TileVisibilityRatioArgs {
  origin: ICoord2D;
  samples: readonly ICoord2D[];
  occluderEdges: readonly ILineSegment[];
}

export interface FilterTilesByVisibilityArgs {
  /** World-space ray origin (typically the caster tile center). */
  origin: ICoord2D;
  /** Candidate tiles to test (e.g. a skill stamp). */
  tiles: readonly ITileGridCoord[];
  /** Occluder edges (e.g. blocker polygon edges). */
  occluderEdges: readonly ILineSegment[];
  tileSize: number;
  /** Sample layout on each candidate tile (default 5). */
  density?: TileVisibilitySampleDensity;
  /** Min visible-sample fraction required for a tile to pass (0–1). */
  threshold: number;
}

export interface FilterTileIndicesByVisibilityArgs extends ITileGrid {
  originTileIndex: number;
  candidateTileIndices: readonly number[];
  occluderTileIndices: readonly number[];
  threshold: number;
  density?: TileVisibilitySampleDensity;
}

/**
 * World-space AABB for the tile at (col, row).
 * Half-open on the max edges: [col·size, (col+1)·size) × [row·size, (row+1)·size).
 */
export function tileWorldRect(col: number, row: number, tileSize: number): IRect {
  return {
    x: col * tileSize,
    y: row * tileSize,
    width: tileSize,
    height: tileSize
  };
}

/** Center of the tile at (col, row) in world space. */
export function tileWorldCenter(col: number, row: number, tileSize: number): ICoord2D {
  return {
    x: (col + 0.5) * tileSize,
    y: (row + 0.5) * tileSize
  };
}

/** Center of the tile at `tileIndex` in world space, or undefined when invalid. */
export function tileIndexWorldCenter(
  tileIndex: number,
  widthTiles: number,
  tilePx: number
): ICoord2D | undefined {
  if (widthTiles <= 0 || tilePx <= 0) {
    return undefined;
  }
  const { col, row } = tileIndexToColRow(tileIndex, widthTiles);
  return tileWorldCenter(col, row, tilePx);
}

/** Floors a world point into a tile column/row (negative floors toward −∞). */
export function worldPointToTile(point: ICoord2D, tileSize: number): ITileGridCoord {
  return {
    col: Math.floor(point.x / tileSize),
    row: Math.floor(point.y / tileSize)
  };
}

/** Row-major tile index for a world point inside the grid, or undefined when out of bounds. */
export function worldPointToTileIndex(point: ICoord2D, tileSize: number, widthTiles: number, heightTiles: number): number | undefined {
  const { col, row } = worldPointToTile(point, tileSize);
  if (col < 0 || row < 0 || col >= widthTiles || row >= heightTiles) {
    return undefined;
  }
  return tileIndexAt(col, row, widthTiles);
}

/**
 * In-bounds cell under a world point on `grid`, or undefined when the point or grid is invalid.
 */
export function worldPointToTileInBounds(point: ICoord2D, grid: ITileGrid): ITileGridCell | undefined {
  if (
    !Number.isFinite(point.x) || !Number.isFinite(point.y)
    || !(grid.tilePx > 0) || !Number.isFinite(grid.tilePx)
    || !Number.isInteger(grid.widthTiles) || grid.widthTiles <= 0
    || !Number.isInteger(grid.heightTiles) || grid.heightTiles <= 0
  ) {
    return undefined;
  }

  const { col, row } = worldPointToTile(point, grid.tilePx);
  if (col < 0 || row < 0 || col >= grid.widthTiles || row >= grid.heightTiles) { return undefined; }
  return { col, row, tileIndex: tileIndexAt(col, row, grid.widthTiles) };
}

export interface WorldRectToTileRangeArgs extends ITileGrid {
  worldRect: IRect;
}

/**
 * Converts a world-space axis-aligned rect into a half-open tile range clamped to the grid.
 * Partial tiles that the rect overlaps are included. Returns undefined when `tilePx` is not > 0.
 */
export function worldRectToTileRange(args: WorldRectToTileRangeArgs): ITileGridRange | undefined {
  const { worldRect, tilePx, widthTiles, heightTiles } = args;
  if (!(tilePx > 0)) {
    return undefined;
  }

  return {
    startCol: clampTileRangeAxis(Math.floor(worldRect.x / tilePx), widthTiles),
    startRow: clampTileRangeAxis(Math.floor(worldRect.y / tilePx), heightTiles),
    endCol: clampTileRangeAxis(Math.ceil((worldRect.x + worldRect.width) / tilePx), widthTiles),
    endRow: clampTileRangeAxis(Math.ceil((worldRect.y + worldRect.height) / tilePx), heightTiles)
  };
}

/** True when the column and row fall inside a half-open tile range. */
export function tileCoordInGridRange(coord: ITileGridCoord, range: ITileGridRange): boolean {
  return colRowInGridRange(coord.col, coord.row, range);
}

/** True when the row-major tile index falls inside a half-open tile range. */
export function tileIndexInGridRange(tileIndex: number, widthTiles: number, range: ITileGridRange): boolean {
  return colRowInGridRange(tileIndex % widthTiles, Math.floor(tileIndex / widthTiles), range);
}

export interface TileIndicesToGridRangeArgs {
  tileIndices: readonly number[];
  widthTiles: number;
  heightTiles: number;
}

/**
 * Half-open union of all valid cells addressed by `tileIndices`.
 * Returns undefined when the list is empty or every index is out of bounds.
 */
export function tileIndicesToGridRange(args: TileIndicesToGridRangeArgs): ITileGridRange | undefined {
  const { tileIndices, widthTiles, heightTiles } = args;
  if (tileIndices.length === 0 || widthTiles <= 0 || heightTiles <= 0) { return undefined; }

  let minCol = widthTiles;
  let minRow = heightTiles;
  let maxCol = 0;
  let maxRow = 0;
  let found = false;

  for (const tileIndex of tileIndices) {
    const col = tileIndex % widthTiles;
    const row = Math.floor(tileIndex / widthTiles);
    if (col < 0 || row < 0 || col >= widthTiles || row >= heightTiles) { continue; }

    found = true;
    minCol = Math.min(minCol, col);
    minRow = Math.min(minRow, row);
    maxCol = Math.max(maxCol, col + 1);
    maxRow = Math.max(maxRow, row + 1);
  }

  if (!found) { return undefined; }

  return {
    startCol: minCol,
    startRow: minRow,
    endCol: maxCol,
    endRow: maxRow
  };
}

/** Visits each row-major tile index inside a half-open grid range. */
export function forEachTileIndexInGridRange(range: ITileGridRange, widthTiles: number, visit: (tileIndex: number) => void): void {
  for (let row = range.startRow; row < range.endRow; row++) {
    for (let col = range.startCol; col < range.endCol; col++) {
      visit(tileIndexAt(col, row, widthTiles));
    }
  }
}

function colRowInGridRange(col: number, row: number, range: ITileGridRange): boolean {
  return col >= range.startCol
    && col < range.endCol
    && row >= range.startRow
    && row < range.endRow;
}

function clampTileRangeAxis(value: number, size: number): number {
  return Math.max(0, Math.min(size, value));
}

export interface OccluderEdgesFromTilesArgs {
  /** Tiles that block line-of-effect. */
  tiles: readonly ITileGridCoord[];
  tileSize: number;
}

/** Blocked tile indices plus merged exterior edges for line-of-effect tests. */
export interface OccluderSetFromTileIndices {
  tileIndices: readonly number[];
  occluderEdges: readonly ILineSegment[];
}

export interface OccluderSetFromTileIndicesArgs {
  tileIndices: readonly number[];
  widthTiles: number;
  tileSize: number;
}

/** Axis-aligned span used while merging collinear exterior tile edges. */
interface AxisAlignedSpan {
  /** Shared Y for a horizontal edge, or shared X for a vertical edge. */
  fixed: number;
  min: number;
  max: number;
}

function tileGridCoordKey(col: number, row: number): string {
  return `${col},${row}`;
}

/**
 * Merges overlapping or abutting spans that share the same fixed axis value.
 * Adjacent blocked tiles then contribute one long outer edge instead of many unit edges.
 */
function mergeAxisAlignedSpans(spans: readonly AxisAlignedSpan[]): AxisAlignedSpan[] {
  if (spans.length === 0) {
    return [];
  }

  const byFixed = new Map<number, IMinMax[]>();
  for (const span of spans) {
    const ranges = byFixed.get(span.fixed);
    if (ranges) {
      ranges.push({ min: span.min, max: span.max });
    }
    else {
      byFixed.set(span.fixed, [{ min: span.min, max: span.max }]);
    }
  }

  const merged: AxisAlignedSpan[] = [];
  for (const [fixed, ranges] of byFixed) {
    ranges.sort((a, b) => (a.min - b.min) || (a.max - b.max));
    let currentMin = ranges[0].min;
    let currentMax = ranges[0].max;
    for (let i = 1; i < ranges.length; i++) {
      const next = ranges[i];
      if (next.min <= currentMax) {
        currentMax = Math.max(currentMax, next.max);
        continue;
      }
      merged.push({ fixed, min: currentMin, max: currentMax });
      currentMin = next.min;
      currentMax = next.max;
    }
    merged.push({ fixed, min: currentMin, max: currentMax });
  }
  return merged;
}

/**
 * Builds occluder edges from blocked tile AABBs.
 * Only exterior sides are kept (shared sides between adjacent blocked tiles are omitted),
 * then collinear abutting sides are merged into longer segments.
 */
export function occluderEdgesFromTiles(args: OccluderEdgesFromTilesArgs): ILineSegment[] {
  const { tiles, tileSize } = args;
  const blocked = new Set<string>();
  for (const tile of tiles) {
    blocked.add(tileGridCoordKey(tile.col, tile.row));
  }

  const horizontal: AxisAlignedSpan[] = [];
  const vertical: AxisAlignedSpan[] = [];

  for (const tile of tiles) {
    const { col, row } = tile;
    const minX = col * tileSize;
    const minY = row * tileSize;
    const maxX = minX + tileSize;
    const maxY = minY + tileSize;

    if (!blocked.has(tileGridCoordKey(col, row - 1))) {
      horizontal.push({ fixed: minY, min: minX, max: maxX });
    }

    if (!blocked.has(tileGridCoordKey(col, row + 1))) {
      horizontal.push({ fixed: maxY, min: minX, max: maxX });
    }

    if (!blocked.has(tileGridCoordKey(col - 1, row))) {
      vertical.push({ fixed: minX, min: minY, max: maxY });
    }

    if (!blocked.has(tileGridCoordKey(col + 1, row))) {
      vertical.push({ fixed: maxX, min: minY, max: maxY });
    }
  }

  const edges: ILineSegment[] = [];
  for (const span of mergeAxisAlignedSpans(horizontal)) {
    edges.push({
      start: { x: span.min, y: span.fixed },
      end: { x: span.max, y: span.fixed }
    });
  }
  for (const span of mergeAxisAlignedSpans(vertical)) {
    edges.push({
      start: { x: span.fixed, y: span.min },
      end: { x: span.fixed, y: span.max }
    });
  }
  return edges;
}

/** Converts blocked tile indices into merged exterior edges for line-of-effect tests. */
export function occluderSetFromTileIndices(args: OccluderSetFromTileIndicesArgs): OccluderSetFromTileIndices {
  const { tileIndices, widthTiles, tileSize } = args;
  const tiles = tileIndices.map((tileIndex) => tileIndexToColRow(tileIndex, widthTiles));
  return {
    tileIndices,
    occluderEdges: occluderEdgesFromTiles({ tiles, tileSize })
  };
}

/**
 * Sample points on `rect` for a visibility ratio test.
 * Corners use the closed max edges (x+width, y+height) so adjacent tiles share exact corner coords
 * and callers can cache corner results across neighbors.
 */
export function tileVisibilitySamplePoints(
  rect: IRect,
  density: TileVisibilitySampleDensity = 5
): ICoord2D[] {
  const minX = rect.x;
  const minY = rect.y;
  const maxX = rect.x + rect.width;
  const maxY = rect.y + rect.height;
  const midX = rect.x + (rect.width / 2);
  const midY = rect.y + (rect.height / 2);
  const center: ICoord2D = { x: midX, y: midY };

  const corners: ICoord2D[] = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: minX, y: maxY },
    { x: maxX, y: maxY }
  ];

  if (density === 1) {
    return [center];
  }

  if (density === 4) {
    return corners;
  }

  if (density === 5) {
    return [center, ...corners];
  }

  return [
    corners[0],
    { x: midX, y: minY },
    corners[1],
    { x: minX, y: midY },
    center,
    { x: maxX, y: midY },
    corners[2],
    { x: midX, y: maxY },
    corners[3]
  ];
}

/**
 * True when the open segment (origin, target) crosses the open interior of any occluder edge.
 * Endpoint touches and collinear grazes do not count — shared corners between open and blocked
 * tiles stay usable, and client/server must share this same rule.
 */
export function isSegmentOccludedByEdges(
  origin: ICoord2D,
  target: ICoord2D,
  occluderEdges: readonly ILineSegment[]
): boolean {
  for (const edge of occluderEdges) {
    if (GeomUtils.segmentsCrossInterior(origin, target, edge.start, edge.end)) {
      return true;
    }
  }
  return false;
}

/**
 * Fraction of sample points with a clear open segment from `origin` (no interior edge cross).
 * Throws when `samples` is empty: zero of zero has no ratio, and any stand-in value would
 * silently pass or fail every tile. Sample sets from {@link tileVisibilitySamplePoints} always
 * hold 1, 4, 5, or 9 points.
 */
export function sampleVisibilityRatio(args: TileVisibilityRatioArgs): number {
  const { origin, samples, occluderEdges } = args;
  if (samples.length === 0) {
    throw new RangeError('sampleVisibilityRatio requires at least one sample point');
  }

  let visible = 0;
  for (const sample of samples) {
    if (!isSegmentOccludedByEdges(origin, sample, occluderEdges)) {
      visible++;
    }
  }
  return visible / samples.length;
}

/** True when `ratio` meets or exceeds `threshold`. */
export function meetsVisibilityThreshold(ratio: number, threshold: number): boolean {
  return ratio >= threshold;
}

/**
 * Returns the subset of `tiles` whose sample visibility ratio meets `threshold`.
 * Uses one {@link VertexVisibilityCache} for the whole pass so shared vertices are raycast once.
 * Per-tile sample checks stop early once the remaining points cannot change the threshold outcome.
 */
export function filterTilesByVisibility(args: FilterTilesByVisibilityArgs): ITileGridCoord[] {
  const density = args.density ?? 5;
  const cache = new VertexVisibilityCache({
    origin: args.origin,
    occluderEdges: args.occluderEdges
  });

  const visibleTiles: ITileGridCoord[] = [];
  for (const tile of args.tiles) {
    const samples = tileVisibilitySamplePoints(
      tileWorldRect(tile.col, tile.row, args.tileSize),
      density
    );
    if (cache.meetsVisibilityThreshold(samples, args.threshold)) {
      visibleTiles.push(tile);
    }
  }
  return visibleTiles;
}

/**
 * Row-major tile-index form of {@link filterTilesByVisibility}.
 * Drops origin, candidates, and occluders outside `[0, widthTiles * heightTiles)`.
 * Empty in-range occluders skip the visibility pass and return a copy of in-range candidates.
 */
export function filterTileIndicesByVisibility(args: FilterTileIndicesByVisibilityArgs): number[] {
  const { originTileIndex, candidateTileIndices, occluderTileIndices, widthTiles, heightTiles, tilePx, threshold, density } = args;

  if (widthTiles <= 0 || heightTiles <= 0 || tilePx <= 0) { return []; }

  const maxIndexExclusive = widthTiles * heightTiles;
  if (originTileIndex < 0 || originTileIndex >= maxIndexExclusive) { return []; }

  const candidates = candidateTileIndices.filter((tileIndex) => tileIndex >= 0 && tileIndex < maxIndexExclusive);
  if (candidates.length === 0) { return []; }

  const occluders = occluderTileIndices.filter((tileIndex) => tileIndex >= 0 && tileIndex < maxIndexExclusive);
  if (occluders.length === 0) { return [...candidates]; }

  const origin = tileIndexWorldCenter(originTileIndex, widthTiles, tilePx);
  if (origin === undefined) { return []; }

  const { occluderEdges } = occluderSetFromTileIndices({
    tileIndices: occluders,
    widthTiles,
    tileSize: tilePx
  });

  const visibleTiles = filterTilesByVisibility({
    origin,
    tiles: candidates.map((tileIndex) => tileIndexToColRow(tileIndex, widthTiles)),
    occluderEdges,
    tileSize: tilePx,
    density,
    threshold
  });

  return visibleTiles.map((tile) => tileIndexAt(tile.col, tile.row, widthTiles));
}

/** Inputs for a per-cast vertex visibility cache (fixed origin + occluder set). */
export interface VertexVisibilityCacheArgs {
  origin: ICoord2D;
  occluderEdges: readonly ILineSegment[];
}

/**
 * Stable map key for an exact world sample point.
 * Adjacent tiles share corner (and density-9 edge-midpoint) coords, so one cast can reuse hits.
 */
export function vertexVisibilityCacheKey(point: ICoord2D): string {
  return `${point.x},${point.y}`;
}

/**
 * Caches whether each sample vertex is visible from a fixed origin through fixed occluder edges.
 * Use one instance per cast (same origin and edges). Tile centers are unique per cell; corners and
 * edge midpoints are shared and hit the cache when neighboring tiles are tested in the same pass.
 */
export class VertexVisibilityCache {
  private readonly origin: ICoord2D;
  private readonly occluderEdges: readonly ILineSegment[];
  private readonly visibleByKey = new Map<string, boolean>();

  public constructor(args: VertexVisibilityCacheArgs) {
    this.origin = args.origin;
    this.occluderEdges = args.occluderEdges;
  }

  /** True when the open segment from the cache origin to `point` is not occluded. */
  public isVisible(point: ICoord2D): boolean {
    const key = vertexVisibilityCacheKey(point);
    const cached = this.visibleByKey.get(key);
    if (cached !== undefined) {
      return cached;
    }
    const visible = !isSegmentOccludedByEdges(this.origin, point, this.occluderEdges);
    this.visibleByKey.set(key, visible);
    return visible;
  }

  /**
   * Same ratio as {@link sampleVisibilityRatio}, but reuses cached vertex results across calls
   * on this instance (e.g. one stamp of many tiles).
   */
  public sampleVisibilityRatio(samples: readonly ICoord2D[]): number {
    if (samples.length === 0) {
      throw new RangeError('VertexVisibilityCache.sampleVisibilityRatio requires at least one sample point');
    }

    let visible = 0;
    for (const sample of samples) {
      if (this.isVisible(sample)) {
        visible++;
      }
    }
    return visible / samples.length;
  }

  /**
   * True when visible samples / sample count meets `threshold`.
   * Stops once remaining samples cannot change the outcome (density fixes the check count).
   */
  public meetsVisibilityThreshold(samples: readonly ICoord2D[], threshold: number): boolean {
    const n = samples.length;
    if (n === 0) {
      throw new RangeError('VertexVisibilityCache.meetsVisibilityThreshold requires at least one sample point');
    }

    let visible = 0;
    for (let i = 0; i < n; i++) {
      if (this.isVisible(samples[i])) {
        visible++;
      }

      const remaining = n - i - 1;
      if (visible >= threshold * n) { return true; }

      if (visible + remaining < threshold * n) { return false; }
    }
    return visible >= threshold * n;
  }

  public clear(): void {
    this.visibleByKey.clear();
  }
}
